import { Controller } from "@hotwired/stimulus"
import { showPageFlash } from "../../../src/application/flash_messages.js"

/**
 * To-do notes UI state machine: button → empty/filled → saved.
 * Save/delete persist only when saveUrl / deleteUrl values are set.
 * Library/demo leave those blank and rely on local Stimulus state + events:
 * `todo-notes:saved`, `todo-notes:deleted`.
 */
export default class extends Controller {
  static targets = [
    "buttonPanel",
    "editorPanel",
    "savedPanel",
    "textarea",
    "savedText",
    "saveButton",
    "addButton"
  ]

  static values = {
    state: { type: String, default: "button" },
    draft: { type: String, default: "" },
    saveUrl: { type: String, default: "" },
    deleteUrl: { type: String, default: "" },
    submitMethod: { type: String, default: "POST" }
  }

  connect() {
    this.draftValue = this.hasTextareaTarget ? this.textareaTarget.value : ""
    this.sync()
  }

  start(event) {
    event?.preventDefault()
    this.stateValue = this.draftValue.trim() ? "filled" : "empty"
    this.focusTextarea()
  }

  input() {
    if (!this.hasTextareaTarget) return
    this.draftValue = this.textareaTarget.value
    this.stateValue = this.draftValue.trim() ? "filled" : "empty"
    this.syncSaveButton()
  }

  cancel(event) {
    event?.preventDefault()
    const saved = this.savedNoteText()
    if (saved) {
      this.stateValue = "saved"
      if (this.hasTextareaTarget) {
        this.textareaTarget.value = saved
        this.draftValue = saved
      }
    } else {
      this.stateValue = "button"
      if (this.hasTextareaTarget) {
        this.textareaTarget.value = ""
        this.draftValue = ""
      }
    }
  }

  save(event) {
    event?.preventDefault()
    if (!this.hasTextareaTarget) return

    const value = this.textareaTarget.value.trim()
    if (!value) return

    const rollback = {
      previousState: this.stateValue,
      previousSaved: this.savedNoteText(),
      previousDraft: this.draftValue
    }
    this.draftValue = value
    if (this.hasSavedTextTarget) {
      this.savedTextTarget.textContent = value
    }
    this.stateValue = "saved"
    this.dispatch("saved", { detail: { value } })
    this.persistSave(value, rollback)
  }

  edit(event) {
    event?.preventDefault()
    if (this.hasSavedTextTarget && this.hasTextareaTarget) {
      const saved = this.savedNoteText()
      this.textareaTarget.value = saved
      this.draftValue = saved
    }
    this.stateValue = this.draftValue.trim() ? "filled" : "empty"
    this.focusTextarea()
  }

  delete(event) {
    event?.preventDefault()
    const rollback = {
      previousState: this.stateValue,
      previousSaved: this.savedNoteText(),
      previousDraft: this.draftValue
    }
    if (this.hasTextareaTarget) {
      this.textareaTarget.value = ""
    }
    if (this.hasSavedTextTarget) {
      this.savedTextTarget.textContent = ""
    }
    this.draftValue = ""
    this.stateValue = "button"
    this.dispatch("deleted")
    this.persistDelete(rollback)
  }

  stateValueChanged() {
    this.sync()
  }

  sync() {
    const state = this.stateValue

    this.togglePanel("buttonPanel", state === "button")
    this.togglePanel("editorPanel", state === "empty" || state === "filled")
    this.togglePanel("savedPanel", state === "saved")
    this.syncSaveButton()
  }

  syncSaveButton() {
    if (!this.hasSaveButtonTarget) return
    this.saveButtonTarget.disabled = !this.draftValue.trim()
  }

  togglePanel(name, visible) {
    const has = this[`has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`]
    if (!has) return
    const el = this[`${name}Target`]
    el.classList.toggle("hidden", !visible)
    el.hidden = !visible
  }

  focusTextarea() {
    if (!this.hasTextareaTarget) return
    requestAnimationFrame(() => this.textareaTarget.focus())
  }

  savedNoteText() {
    if (!this.hasSavedTextTarget) return ""
    return this.savedTextTarget.textContent.trim()
  }

  persistSave(value, rollback) {
    this.persistNote(value, rollback)
  }

  persistDelete(rollback) {
    if (this.deleteUrlValue) {
      window.fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: this.requestHeaders()
      })
        .then((response) => this.handlePersistResponse(response, rollback))
        .catch(() => {
          this.rollbackNote(rollback)
          showPageFlash("Unable to delete the note.")
        })
      return
    }

    this.persistNote("", rollback)
  }

  persistNote(value, rollback) {
    if (!this.saveUrlValue) return

    window.fetch(this.saveUrlValue, {
      method: (this.submitMethodValue || "POST").toUpperCase(),
      headers: this.requestHeaders(),
      body: JSON.stringify({ note: value })
    })
      .then((response) => this.handlePersistResponse(response, rollback))
      .catch(() => {
        this.rollbackNote(rollback)
        showPageFlash("Unable to update the note.")
      })
  }

  handlePersistResponse(response, rollback) {
    if (response.ok) return

    this.rollbackNote(rollback)
    showPageFlash("Unable to update the note.")
  }

  rollbackNote(rollback = {}) {
    if (rollback.previousState) this.stateValue = rollback.previousState
    if (this.hasSavedTextTarget) {
      this.savedTextTarget.textContent = rollback.previousSaved || ""
    }
    if (this.hasTextareaTarget) {
      const restored = rollback.previousState === "saved"
        ? (rollback.previousSaved || "")
        : (rollback.previousDraft || "")
      this.textareaTarget.value = restored
      this.draftValue = restored
    }
  }

  requestHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
    }
  }
}
